import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, ColorInput, Group, Image, Select, Stack, Text, TextInput } from "@mantine/core"
import { LEX_THEME_KEYS, LEX_THEME_LABELS } from "../../lib/lex-themes"
import { BrandPreview } from "../BrandPreview"
import type { IProps } from "./interfaces"
import classes from "./styles.module.css"

function isPreviewableUrl(value: string): boolean {
  const trimmed = value.trim().toLowerCase()
  return trimmed.startsWith("https://") && !trimmed.includes(" ")
}

/**
 * Logo, brand colour, login background, and (for Lexicanon) the default theme.
 * Props in, events out. The company page and the settings Look section both
 * render this; each one owns the save.
 */
export function BrandingFormView({
  logoUrl,
  brandColor,
  loginBackgroundUrl,
  defaultTheme,
  readOnly,
  saving,
  showTheme,
  showIdentity,
  name,
  slug,
  onLogoUrlChange,
  onBrandColorChange,
  onLoginBackgroundUrlChange,
  onDefaultThemeChange,
  onNameChange,
  onSlugChange,
  onSave,
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  const showLogo = isPreviewableUrl(logoUrl)
  const showBackground = isPreviewableUrl(loginBackgroundUrl)

  return (
    <Stack gap="sm">
      {showIdentity && (
        <Group grow>
          <TextInput
            label={i18n._(msg`Name`)}
            value={name}
            onChange={(event) => onNameChange(event.currentTarget.value)}
            disabled={readOnly}
          />
          <TextInput
            label={i18n._(msg`Slug (subdomain)`)}
            value={slug}
            onChange={(event) => onSlugChange(event.currentTarget.value)}
            disabled={readOnly}
          />
        </Group>
      )}
      <TextInput
        label={i18n._(msg`Logo URL`)}
        description={i18n._(msg`An https link to the image. It shows on the sign-in page.`)}
        value={logoUrl}
        onChange={(event) => onLogoUrlChange(event.currentTarget.value)}
        placeholder="https://"
        disabled={readOnly}
      />
      {showLogo && (
        <Image src={logoUrl.trim()} alt={i18n._(msg`Logo preview`)} className={classes.preview} fit="contain" />
      )}
      <ColorInput
        label={i18n._(msg`Brand colour`)}
        description={i18n._(msg`One colour. The rest of the palette is worked out from it.`)}
        value={brandColor}
        onChange={onBrandColorChange}
        format="hex"
        disabled={readOnly}
      />
      <Stack gap={6}>
        <Text size="sm" fw={500}>
          <Trans>Brand preview</Trans>
        </Text>
        <BrandPreview brandColor={brandColor} />
      </Stack>
      <TextInput
        label={i18n._(msg`Login background URL`)}
        description={i18n._(msg`Optional https image behind the sign-in page.`)}
        value={loginBackgroundUrl}
        onChange={(event) => onLoginBackgroundUrlChange(event.currentTarget.value)}
        placeholder="https://"
        disabled={readOnly}
      />
      {showBackground && (
        <Image
          src={loginBackgroundUrl.trim()}
          alt={i18n._(msg`Login background preview`)}
          className={classes.backgroundPreview}
          fit="cover"
        />
      )}
      {showTheme && (
        <Select
          label={i18n._(msg`Default theme`)}
          description={i18n._(msg`Used by people who haven't picked their own theme, and on the sign-in page.`)}
          value={defaultTheme}
          onChange={(value) => onDefaultThemeChange(value ?? "")}
          allowDeselect={false}
          disabled={readOnly}
          data={[
            { value: "", label: i18n._(msg`Global default (Pastel Calm)`) },
            ...LEX_THEME_KEYS.map((key) => ({ value: key, label: LEX_THEME_LABELS[key] })),
          ]}
          maw={320}
        />
      )}
      {readOnly ? (
        <Text size="sm" c="dimmed">
          <Trans>You can view this, but only an owner or admin can change it.</Trans>
        </Text>
      ) : (
        <Group>
          <Button onClick={onSave} loading={saving}>
            <Trans>Save</Trans>
          </Button>
        </Group>
      )}
    </Stack>
  )
}
